// @flow
import React, { useState, useCallback } from "react";
import ButtonToolbar from "react-bootstrap/lib/ButtonToolbar";
import Button from "react-bootstrap/lib/Button";
import EAForm from "components/ExtraAttributes/EAForm";
import Formsy, { addValidationRule } from "formsy-react";
import { type SearchFormResultDTO } from "data/types";
import AttributeSearch from "./AttributeSearch";
import "components/Form/index.css";
import "./index.css";
import { getClassNames } from "data/utils";
import { LabelSizeContext } from "data/context";
import { useEffectOnce } from "react-use";

const EASearchForm = (props) => (
    <EAForm
        isEditMode={true}
        fieldComponentClass={AttributeSearch}
        {...props}
    />
);

type Props = {
    /** result itemUri */
    itemUri: string,
    /** searchForm definition */
    form: SearchFormResultDTO,
    /** Search button label */
    searchLabel: string,
    /** whether the Search button should be enabled */
    searchEnabled?: boolean,
    /** whether to show search operators */
    showSearchOperators?: boolean,
    // /** callback when user clicks the Cancel button */
    // onCancel: () => void,
    /** callback when user clicks the Search button */
    onSearch: (model: Object) => void,
    /** callback when an attribute is edited
     * @param {string} name unique name of attribute being changed
     * @param {any} value new value
     */
    onChange: (name: string, value: any) => void,
    /** callback when a validation is finished on the form (could be valid or invalid) */
    onValidated?: (isValid: boolean) => void,
};

const SearchForm = (props: Props) => {
    const [isValid, setIsValid] = useState(true);
    const [isChanged, setIsChanged] = useState(true);

    useEffectOnce(() => {
        /* istanbul ignore next */
        addValidationRule(
            "isNotInvalid",
            (values, value) =>
                value == null || !String(value).startsWith("__INVALID__")
        );
    });

    const _onValidChanged = useCallback((valid: boolean) => {
        setIsValid(valid);
        isChanged &&
            props.onValidated &&
            props.onValidated(valid);
    }, [setIsValid, isChanged, props]);

    /* istanbul ignore next */
    const _onChange = useCallback((currentValues, changed) => changed && !isChanged && setIsChanged(true), [isChanged, setIsChanged]);

    return (<Formsy
        className={getClassNames(
            "form-inline",
            "ip-form",
            "form-edit",
            "search-form",
            props.showSearchOperators === true
                ? null
                : "search-op-hidden"
        )}
        onValidSubmit={props.onSearch}
        /* istanbul ignore next */
        onValid={() => _onValidChanged(true)}
        onInvalid={/* istanbul ignore next */() => _onValidChanged(false)}
        onChange={_onChange}
    >
        <LabelSizeContext.Provider value={125}>
            <EASearchForm
                key="ea-sf"
                itemUri={props.itemUri}
                layoutXml={props.form.layoutXml}
                attributes={props.form.attributes}
                onChange={props.onChange}
            />
        </LabelSizeContext.Provider>

        <ButtonToolbar className="form-actions">
            <Button
                type="submit"
                bsStyle={isValid ? "primary" : "default"}
                disabled={!(isValid && props.searchEnabled)}
                style={{ padding: "6px 30px" }}
                        data-test="searchFormSubmitButton"
            >
                {props.searchLabel}
            </Button>
        </ButtonToolbar>
    </Formsy>);
}

export default SearchForm